// Search input handling and initialization

import { getSearchTimeout, setSearchTimeout, clearSearchTimeout } from './state.js';
import { updateFulltextRequestMismatchIndicator } from './request-indicator.js';

/**
 * Initialize search input handlers
 */
export async function initializeSearchInput() {
    const searchInput = $('#fnd_searchbox, #search-input, .search-input, input[name="search"], input[name="sq"]');
    if (searchInput.length === 0) return;

    // Export function to window for access from other modules
    if (typeof window !== 'undefined') {
        window.updateFulltextRequestMismatchIndicator = updateFulltextRequestMismatchIndicator;
    }

    // Inline ghost-text autocomplete (suppresses native browser autocomplete dropdown,
    // surfaces last-used queries from local history). Component is loaded synchronously
    // by `search_dev_scripts.html` before this ES module runs.
    //
    // mode: 'manual' — we don't save on Enter/blur (which would pollute history with
    // every keystroke commit). Instead we save only when the user took a "useful
    // action" against a result: clicked a file link, opened a preview, or opened the
    // AI summary popup. The query that led to the action is the one worth remembering.
    const SEARCH_AUTOCOMPLETE_OPTS = { historyKey: 'search_history_v2', mode: 'manual' };
    try {
        const targetEl = searchInput[0];
        if (targetEl && window.SearchAutocomplete && typeof window.SearchAutocomplete.init === 'function') {
            window.SearchAutocomplete.init(targetEl, SEARCH_AUTOCOMPLETE_OPTS);
        }
    } catch (_) {}

    // Commit history on file-action clicks anywhere in the document. Selectors cover
    // every "user used the result" path on the search page:
    //   .file-link              — click on the file name (download / open)
    //   [data-action="preview"] — preview pane (audio playback, document preview)
    //   [data-action="summary"] — AI summary popup
    //   [data-action="download"]— explicit download button (if rendered)
    //
    // CRITICAL: bound on the CAPTURE phase, not bubble. `file-list.dom/events.js`
    // calls `stopPropagation()` / `stopImmediatePropagation()` on .file-link and
    // related elements during the bubble phase (closer to the target than document),
    // which would have killed a bubble-phase listener here. Capture phase fires on
    // ancestors BEFORE the event reaches the target, so we see every click first.
    if (!window.__searchAutocompleteCommitWired) {
        window.__searchAutocompleteCommitWired = true;
        // Selectors covering every "user took useful action against a result" path on the
        // search page: opens a file (.file-link), opens preview / AI summary / download,
        // or follows a /library/ /online/ /api/files/ link. Mousedown + click both bound
        // because file-list events.js calls stopImmediatePropagation on bubble — capture
        // beats it, and mousedown fires before click in case click gets blocked entirely.
        const COMMIT_SELECTOR = [
            '.file-link',
            '[data-action]',
            'a[href*="/library/"]',
            'a[href*="/online/"]',
            'a[href*="/api/files/"]',
            '.summary-rail',
            '.preview-icon',
            '.summary-icon',
            '.audio-preview-icon'
        ].join(', ');
        function commitFromAction(ev) {
            try {
                const t = ev.target;
                if (!t || !t.closest) return;
                const matched = t.closest(COMMIT_SELECTOR);
                if (!matched) return;
                const inputEl = document.getElementById('fnd_searchbox')
                    || document.querySelector('#search-input, .search-input, input[name="search"], input[name="sq"]');
                if (!inputEl) return;
                const q = (inputEl.value || '').toString().trim();
                if (q && window.SearchAutocomplete && typeof window.SearchAutocomplete.saveQuery === 'function') {
                    window.SearchAutocomplete.saveQuery(q, SEARCH_AUTOCOMPLETE_OPTS);
                }
            } catch (_) {}
        }
        document.addEventListener('mousedown', commitFromAction, true);
        document.addEventListener('click', commitFromAction, true);
    }

    if (window.QUERY && typeof window.QUERY === 'string' && window.QUERY.trim()) {
        searchInput.val(window.QUERY);
        const checkAndTriggerSearch = () => {
            const __files = (window.filesStore && typeof window.filesStore.getFiles==='function') ? window.filesStore.getFiles() : [];
            if (__files && __files.length > 0) {
                if (window.filterManager && typeof window.filterManager.handleSearchInput === 'function') {
                    window.filterManager.handleSearchInput();
                }
            } else { setTimeout(checkAndTriggerSearch, 100); }
        };
        setTimeout(checkAndTriggerSearch, 200);
    }

    searchInput.off('input.appInit keyup.appInit').on('input.appInit keyup.appInit', function(e){
        handleSearchInput(e);
        try { updateFulltextRequestMismatchIndicator($(this).val()); } catch(_) {}
    });
}

/**
 * Handle search input event
 * @param {Event} event - Input event
 */
export function handleSearchInput(event) {
    const input = $(event.target);
    const query = input.val().trim();
    window.QUERY = query;
    
    // Update filter controller search query (URL will be updated when search is executed)
    if (window.filterController) {
        window.filterController.activeFilters.searchQuery = query;
    }
    
    if (query.length >= 2) debounceSearch(query);
}

/**
 * Handle search form submit
 * @param {Event} event - Submit event
 */
export function handleSearchSubmit(event) {
    event.preventDefault();
    const form = $(event.target);
    const input = form.find('input[type="search"], input[name="search"]').first();
    const query = (input.val() || '').toString().trim();
    if (query.length > 0) { 
        window.QUERY = query; 
        executeSearch(query); 
    }
}

/**
 * Debounce search execution
 * @param {string} query - Search query
 */
export function debounceSearch(query) {
    clearSearchTimeout();
    setSearchTimeout(setTimeout(() => { executeSearch(query); }, 300));
}

/**
 * Execute search via search engine
 * @param {string} query - Search query
 */
export function executeSearch(query) {
    try {
        if (window.searchEngine && window.searchEngine.search) {
            window.searchEngine.search(query);
        } else if (window.performSearch && typeof window.performSearch === 'function') {
            window.performSearch(query);
        }
    } catch (_) {}
}

